import type React from 'react';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';
import type { FunctionDetails } from '@lib/models/ServerEvent';

// Logical grouping for renderer dispatch. Multiple EventTypes can map to
// the same kind — notably, the three function event types share a single
// `'function'` renderer because they're the same part mutating through
// states. `'boxed'` is the catch-all for event types that today render
// through `MessageNonTextDisplay` (InlineData, FileData, code execution,
// etc.).
export type PartKind =
    | 'text'
    | 'thought'
    | 'function'
    | 'waiting'
    | 'suspended'
    | 'boxed'
    // UI render tools (server marks them with a `UI_` name prefix / later a
    // dedicated UIFunctionCall content type). First-class content: rendered by
    // a host-provided renderer and NOT gated by `showToolCalls`.
    | 'uiTool'
    // ask_user: a yes/no question rendered with Yes/No buttons inside the library
    // (built-in, not host-provided like uiTool). Routed by function name, not isUi.
    | 'askUser'
    // askauth: an inline "sign in to continue" card (built-in). On click it invokes the
    // host-supplied `onRequestAuth` login flow and sends the returned signed token back
    // via answerHumanTool. Routed by function name, like askUser.
    | 'askAuth';

export interface PartRendererProps {
    part: InteractionPart;
    message: Interaction;
    onGoing: boolean;
    preview: boolean;
    isLastInlineInGroup?: boolean;
    friendlyMode?: boolean;
    onLinkClicked: (link: string, content: string | null) => void;
}

export type PartRenderer = React.ComponentType<PartRendererProps>;
export type PartRendererMap = Partial<Record<PartKind, PartRenderer>>;

/**
 * Match the ask_user tool by function name across connectors. The programmed
 * path emits the bare `ask_user`; live LLM connectors qualify it as
 * `{Plugin}-ask_user` / `{Plugin}_ask_user` (with an empty pluginName), so a
 * literal `=== 'ask_user'` misses real-model calls.
 */
export const isAskUser = (name?: string | null): boolean =>
    !!name && (name === 'ask_user' || name.endsWith('-ask_user') || name.endsWith('_ask_user'));

/**
 * Match the askauth tool by function name across connectors. The programmed path emits the
 * bare `askauth`; live LLM connectors qualify it as `{Plugin}-askauth` / `{Plugin}_askauth`.
 */
export const isAskAuth = (name?: string | null): boolean =>
    !!name && (name === 'askauth' || name.endsWith('-askauth') || name.endsWith('_askauth'));

/** Built-in ask-user component names (the server's [KernelUIComponent] values for the SDK tools). */
export const ASK_USER_COMPONENT = 'AskUserPart';
export const ASK_AUTH_COMPONENT = 'AskAuthPart';

/**
 * True when a function part is an ask-user (human-in-the-loop) prompt — drawn in the docked
 * AskUserSheet, not inline as tool chatter. Prefers the first-class server flag
 * (`functionDetails.isAskUser`, from [KernelUIComponent]); falls back to the legacy hard-coded
 * ask_user/askauth name match so a pre-flag server still routes correctly.
 */
export const isAskUserPart = (fd?: FunctionDetails | null): boolean =>
    !!fd && (fd.isAskUser === true || isAskUser(fd.name) || isAskAuth(fd.name));

/**
 * The ask-user component to draw for a part: the server-stamped `askUserComponent` when present,
 * else the legacy name-based default (AskAuthPart for askauth, AskUserPart otherwise). Returns
 * `undefined` when the part is not an ask-user part.
 */
export const askUserComponentOf = (fd?: FunctionDetails | null): string | undefined => {
    if (!fd) return undefined;
    if (fd.askUserComponent) return fd.askUserComponent;
    if (isAskAuth(fd.name)) return ASK_AUTH_COMPONENT;
    if (isAskUser(fd.name)) return ASK_USER_COMPONENT;
    return undefined;
};
